import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DocumentsContainerComponent, DocumentsTableColumnModel, MenuPanelComponent, MenuPanelItemModel } from '../../../shared/components';
import { buildRowContextMenuItems, getBulkDocTypeName, withBulkActionSpinner } from '../../../shared/helpers';
import { ContextMenuItemAction } from 'common/src/models/context-menu.model';
import { getConfigColumns } from './config-columns';
import { TABLE_BADGE_CLASS } from '../../../shared/constants/table-badge-class';
import { ENTITY_ROUTE_PATHS } from '../../../shared/constants/entity-route-paths';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FileSaverService } from 'ngx-filesaver';
import { TranslateService } from '@ngx-translate/core';
import { get, isEqual } from 'lodash';

import { NavBarBtnModel, PaginationModel, UIStatesEnum, UserModel } from 'common/src/models';
import { HrmService } from '../../hrm.service';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { TabDefinitionModel } from 'common/src/modules/ui-components/nav-tabs/tab-definition.model';
import { TabUserList } from './user-list-tab';
import { generateTableColumns } from './user-list-columns';
import { CreateNewEmployeeModalComponent } from 'common/src/modules/modals/modals-hrm';
import { UserListTabs } from './enums/user-list-tabs.enum';
import { STATUS_KEY, TABS_CAN_ADD_EMPLOYEE } from './user-list.constants';
import { FilterModel } from '../../../warehouse/models/filter.model';
import { ResponseList, UserSorting } from '../../../shared/models/response';
import { employeeResponseHelper } from './helpers/response.helper';
import { TableColumnModelExtended } from 'common/src/models/table-column.model';
import {
  CommonModalsActionsEnum,
  DangerModalComponent,
  InfoModalComponent,
  WarningModalComponent
} from 'common/src/modules/modals/modals-common';
import { ActionBarButtons, EMPTY_STATE_DATA, getTableSummaryBarItems, getTeamsListFilters } from './user-list.config';
import { getMyProfileNavBarButtons, getUserNavBarButtons } from '../employee-profile/employee-profile.config';
import { TeamsState } from '../../store/reducers';
import { AppState } from '../../../store/state/app.state';
import { selectTeamsList } from '../../store/selectors';
import { DEFAULT_PAGINATION, DEFAULT_SORT_DIRECTION } from '../../../shared/constants';
import { TableActivateTypes } from 'common/src/modules/ui-components/table/custom-table.enums';
import { TableSummaryBarItemModel } from 'common/src/modules/ui-components/table-summary-bar/table-summary-bar.model';
import { FileUploadParams } from 'common/src/models/file-upload-params.model';
import { getContentDispositionFileName } from 'common/src/modules/rnpl-common/helpers';
import { FileService } from 'common/src/services/file.service';
import { selectCompanyProfile, selectOnboarding } from '../../../administration/store/selectors';
import { CompanyProfile } from '../../../administration/models/company-profile.model';
import { OnboardingModel } from '../../../administration/models/onboarding.model';
import { AdministrationsApiService } from '../../../administration/services/administrations-api.service';
import { StatusTranslatePipe } from 'common/src/modules/rnpl-common';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { UserSortingService } from '../../../shared/services';
import { FiltersGroupModel, FiltersService } from 'common/src/modules/filters';
import { SubscriptionPlanEnum } from 'common/src/modules/subscription-activation/subscription-activation.emum';
import { ActionButtonsService } from 'common/src/services/action-buttons.service';
import { SbcPermissionEnum } from '../../../administration/enums/sbc-permission.enum';
import { AuthService } from 'common/src/auth/auth.service';


@Component({
  selector: 'rnpl-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, OnDestroy {
  public onboarding: OnboardingModel;
  readonly activeStatus$: BehaviorSubject<UserListTabs> = new BehaviorSubject<UserListTabs>(UserListTabs.Active);
  readonly isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isLoadingSyncRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly canCreateEmployee$: Observable<boolean>;
  readonly usersList$: BehaviorSubject<UserModel[]> = new BehaviorSubject([]);
  private listData$: BehaviorSubject<TeamsState> = new BehaviorSubject(null);
  public sort: FilterModel = { nameColumn: 'updatedAt', value: DEFAULT_SORT_DIRECTION };
  public documentTypesUppercaseEnum = DocumentTypesUppercaseEnum;

  public emptyStateData = EMPTY_STATE_DATA;

  public tabs: Array<TabDefinitionModel> = TabUserList;
  public pagination: PaginationModel = DEFAULT_PAGINATION;
  public actionBarButtons: NavBarBtnModel[] = withBulkActionSpinner(ActionBarButtons
    .map(btn => ({ ...btn, multipleClasses: 'btn-white' })));
  public navBarButtons: NavBarBtnModel[] = [];
  public activatedRow: UserModel = null;
  public bulkSelectionAvailable = true;
  public rowContextMenuItems: MenuPanelItemModel[] = [];

  public selectedRows = [];
  public oldColumns: TableColumnModelExtended[];
  public customizedColumns: TableColumnModelExtended[] = [];
  public columns: DocumentsTableColumnModel[] = [];
  public columnsConfig: DocumentsTableColumnModel[] = [];
  public badgeClasses = TABLE_BADGE_CLASS;
  public tableSummaryBarItems: TableSummaryBarItemModel[] = [];
  public filtersConfig: FiltersGroupModel[] = [];

  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);
  readonly exportCsvRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly updatedAt$: BehaviorSubject<Date> = new BehaviorSubject<Date>(new Date());
  readonly btnToClearLoadingStatus$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly createNewEmployeeRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  @ViewChild('documentsContainer', {static: false}) documentsContainer: DocumentsContainerComponent;
  @ViewChild('rowContextMenuRef', {static: false}) rowContextMenuRef: MenuPanelComponent;

  constructor(
    private hrmService: HrmService,
    private administrationsApiService: AdministrationsApiService,
    private activatedRoute: ActivatedRoute,
    private toasterService: ToasterService,
    private dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private router: Router,
    private titleService: Title,
    private readonly store: Store<AppState>,
    private location: Location,
    private readonly translateService: TranslateService,
    private readonly statusTranslatePipe: StatusTranslatePipe,
    private readonly fileService: FileService,
    private readonly fileSaverService: FileSaverService,
    private userSortingService: UserSortingService,
    private filtersService: FiltersService,
    private actionButtonsService: ActionButtonsService,
    private readonly authService: AuthService,
  ) {
    this.canCreateEmployee$ = this.activatedRoute.paramMap
      .pipe(
        filter((params: ParamMap) => params.has(STATUS_KEY)),
        map((params: ParamMap) => TABS_CAN_ADD_EMPLOYEE.includes(params.get(STATUS_KEY) as UserListTabs))
      );

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {

        if (!companyProfile.onboardingCompleted) {
          this.administrationsApiService.getOnboardingProcess()
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        }
      });

    this.store.select(selectOnboarding)
      .pipe(takeUntil(this.destroy$))
      .subscribe((onboarding: OnboardingModel) => {
        this.onboarding = onboarding;
      });
  }

  ngOnInit(): void {
    this.setTitle();

    this.bindTeamsList();

    this.activatedRoute.paramMap.pipe(takeUntil(this.destroy$)).subscribe(
      (params: ParamMap) => {
        const pageFromRouting = Number(params.get('page'));

        if (this.activeStatus !== params.get(STATUS_KEY)) {
          this.sort = { nameColumn: 'updatedAt', value: DEFAULT_SORT_DIRECTION };
        }
        this.activeStatus$.next(params.get(STATUS_KEY) as UserListTabs);
        this.selectedRows = [];
        this.tableSummaryBarItems = [];
        this.oldColumns = generateTableColumns(this.activeStatus);
        this.columns = getConfigColumns(this.activeStatus);
        this.columnsConfig = this.columns;
        this.navBarButtons = this.buildNavBarButtons();
        this.pagination = { ...this.pagination, page: pageFromRouting };

        if (this.documentsContainer && this.documentsContainer.filters) {
          this.documentsContainer.filters.closeSidebar();
        }

        // const usersByCurrentPage = this.listData$.getValue()[this.activeStatus][pageFromRouting];
        //
        // if (usersByCurrentPage && usersByCurrentPage.data && isEqual(this.sort, usersByCurrentPage.sort)) {
        //   this.isLoading$.next(false);
        //   this.isLoadingSyncRequest$.next(false);
        //   this.pagination = usersByCurrentPage.pagination;
        //   this.sort = usersByCurrentPage.sort;
        //   this.tableSummaryBarItems = getTableSummaryBarItems(usersByCurrentPage.totals);
        //   this.usersList$.next(employeeResponseHelper(usersByCurrentPage.data, this.translateService));
        //   this.getEmployeeList(true, this.pagination.page, );
        //   this.cdr.detectChanges();
        // } else {
        //   this.pagination = { ...this.pagination, page: pageFromRouting };
        //   this.getEmployeeList();
        // }

        this.filtersConfig = getTeamsListFilters(
          this.hrmService.getRunpleIdsList(),
          this.activeStatus
        );
        this.getEmployeeList();
        this.getUserCounters();
        this.cdr.detectChanges();
      }
    );
  }

  public setTitle(): void {
    const activeStatus = this.translateService.instant(this.statusTranslatePipe.transform(this.activeStatus));
    this.titleService.setTitle(
      `${this.translateService.instant('APP.EMPLOYEES')}: ${activeStatus}`
    );
  }

  public getEmployeeList(preventLoading = false, page = this.pagination.page): void {
    this.selectedRows = [];
    this.isLoadingSyncRequest$.next(true);

    if (!preventLoading) {
      this.isLoading$.next(true);
    }

    this.userSortingService.getUserSorting(this.activeStatus, DocumentTypesUppercaseEnum.USER)
      .pipe(
        tap((sorting: UserSorting) => {
          this.sort = {
            nameColumn: sorting.sortBy || this.sort.nameColumn,
            value: sorting.direction || this.sort.value
          };
          const usersByCurrentPage = this.listData$.getValue()[this.activeStatus][page];
          if (usersByCurrentPage && isEqual(this.sort, usersByCurrentPage.sort)) {
            this.pagination = usersByCurrentPage.pagination;
            this.sort = usersByCurrentPage.sort;
            this.tableSummaryBarItems = getTableSummaryBarItems(usersByCurrentPage.totals);
            this.usersList$.next(employeeResponseHelper(usersByCurrentPage.data, this.translateService));
            this.isLoading$.next(false);
          }
        }),
        switchMap(() => this.hrmService.getUsersV2(
          this.activeStatus,
          page,
          this.pagination.per_page,
          this.sort,
          this.filtersService.getFilters()
        )),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (data: ResponseList<UserModel>) => {
          if (data.status && data.status !== this.activeStatus) { return; }

          this.usersList$.next(employeeResponseHelper(data.data, this.translateService));
          this.pagination = get(data, 'pagination', DEFAULT_PAGINATION);
          this.isLoading$.next(false);
          this.isLoadingSyncRequest$.next(false);

          this.setTitle();

          this.tableSummaryBarItems = getTableSummaryBarItems(data.totals);
          this.updatedAt$.next(new Date());

          // const url = `/team/employee/list/${this.activeStatus}/${this.pagination.page}`;
          // this.location.go(url); // update url without reloading
        },
        (error) => {
          this.updatedAt$.next(null);
          this.isLoading$.next(false);
          this.isLoadingSyncRequest$.next(false);
          this.displayMessage(error.error.message, 'error');
        }
      );
  }

  public bindTeamsList() {
    this.store.select(selectTeamsList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((teamsList) => {
        this.listData$.next(teamsList);
      });
  }

  public getUserCounters() {
    this.hrmService.getUsersCounters()
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.tabs = this.tabs.map((t: TabDefinitionModel) => ({ ...t, count: response[t.tabName] }));
        this.cdr.detectChanges();
      });
  }

  private buildNavBarButtons(): NavBarBtnModel[] {
    return [
      {
        classes: 'btn-white',
        text: 'BUTTON.EXPORT_LIST',
        iconName: 'cloud-download',
        allowLoadingSpinner: true,
        actionName: 'onExportCsvClick',
      },
      {
        classes: 'btn-primary',
        text: 'TEAM.CREATE_EMPLOYEE',
        iconName: 'plus',
        allowLoadingSpinner: true,
        actionName: 'createNewEmployee',
      },
    ];
  }

  public onNavBarAction(btnAction: string): void {
    if (this[btnAction]) {
      this[btnAction]();
    }
  }

  public rowClick(row: UserModel): void {
    this.activatedRow = row;
    this.router.navigate([`/team/employee-profile/${row.id}`]);
  }

  public rowCommands = (row: UserModel): any[] => [`${ENTITY_ROUTE_PATHS.EMPLOYEE_PROFILE}${row.id}`];

  public onRowContextMenu({ row, event }: { row: UserModel; event: MouseEvent }): void {
    event.preventDefault();
    event.stopPropagation();
    this.activatedRow = row;

    const isOwnRow = row.id === this.authService.getUser().id;
    const getNavBarButtons = isOwnRow ? getMyProfileNavBarButtons : getUserNavBarButtons;

    if (this.hasEditingFlag(row)) {
      const editBtn = getNavBarButtons(UIStatesEnum.VIEW).find(btn => btn.actionName === 'onEditClick');
      const editAvailable = !!editBtn && (!editBtn.hideWithStatus || !editBtn.hideWithStatus.includes(row.status));
      this.rowContextMenuItems = [
        { id: 'openDocument', label: 'BUTTON.OPEN_LINK', icon: 'arrow-right' },
        ...(editAvailable ? [{ id: 'onEditClick', label: 'BUTTON.EDIT', icon: 'edit' }] : []),
      ];
    } else {
      const navBarButtons = getNavBarButtons(UIStatesEnum.VIEW)
        .filter(btn => !btn.hideWithStatus || !btn.hideWithStatus.includes(row.status));
      const editBtn = navBarButtons.find(btn => btn.actionName === 'onEditClick');
      const footer = navBarButtons.filter(btn => btn !== editBtn);
      const editActions = editBtn ? [{ icon: 'edit', title: 'edit', action: ContextMenuItemAction.EDIT, disabled: false }] : [];
      this.rowContextMenuItems = buildRowContextMenuItems(footer, editActions, false, []);
    }

    if (this.rowContextMenuItems.length) {
      this.rowContextMenuRef.openAt(event);
    }
  }

  private hasEditingFlag(row: UserModel): boolean {
    return !!((row as any).flags || []).some((flag: string) => (flag || '').toLowerCase() === 'editing');
  }

  public get bulkActionableSelectedRows(): UserModel[] {
    return this.selectedRows.filter((row: UserModel) => !this.hasEditingFlag(row));
  }

  public get bulkActionsButtonsForStatus(): NavBarBtnModel[] {
    return this.bulkActionableSelectedRows.length ? this.actionBarButtons : [];
  }

  public onRowContextMenuItemSelected(item: MenuPanelItemModel): void {
    const row = this.activatedRow;
    if (!row) { return; }

    switch (item.id) {
      case 'openDocument': this.router.navigate([`/team/employee-profile/${row.id}`]); break;
      case 'onEditClick': this.onRowContextEditClick(row); break;
      case 'onDeleteClick': this.onRowContextDeleteClick(row); break;
      case 'onDeletePermanentlyClick': this.onRowContextDeletePermanentlyClick(row); break;
      case 'onActivateClick': this.onRowContextActivateClick(row); break;
      case 'onRestoreClick': this.onRowContextRestoreClick(row); break;
      case 'onDeactivateClick': this.onRowContextDeactivateClick(row); break;
    }
  }

  private onRowContextDeleteClick(row: UserModel): void {
    this.dialog.open(DangerModalComponent, {
      data: {
        title: 'MODAL.DELETE',
        message: this.translateService.instant('TEAM.DELETE_USER', { firstName: row.firstName, lastName: row.lastName }),
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2',
      },
    }).afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.changeSingleUserStatus(row.id, UserListTabs.Deleted);
      }
    });
  }

  private onRowContextDeletePermanentlyClick(row: UserModel): void {
    this.dialog.open(DangerModalComponent, {
      data: {
        title: 'MODAL.DELETE',
        message: this.translateService.instant('TEAM.DELETE_PERMANENTLY_USER', { firstName: row.firstName, lastName: row.lastName }),
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2',
      },
    }).afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.hrmService.deleteUser(row.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.getEmployeeList(true));
      }
    });
  }

  private onRowContextActivateClick(row: UserModel): void {
    if (this.actionButtonsService.isActionDisabledByFreePlan(SbcPermissionEnum.TEAM_USERS, () => this.onRowContextActivateClick(row))) { return; }
    this.dialog.open(WarningModalComponent, {
      data: {
        title: 'MODAL.ACTIVATE',
        message: this.translateService.instant('TEAM.ACTIVATE_USER', { firstName: row.firstName, lastName: row.lastName }),
        confirmBtnIcon: 'checkmark',
      },
    }).afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.changeSingleUserStatus(row.id, UserListTabs.Active);
      }
    });
  }

  private onRowContextRestoreClick(row: UserModel): void {
    if (this.actionButtonsService.isActionDisabledByFreePlan(SbcPermissionEnum.TEAM_USERS, () => this.onRowContextRestoreClick(row))) { return; }
    this.dialog.open(WarningModalComponent, {
      data: {
        title: 'MODAL.RESTORE',
        message: this.translateService.instant('TEAM.RESTORE_USER', { firstName: row.firstName, lastName: row.lastName }),
        confirmBtnText: 'BUTTON.RESTORE',
        confirmBtnIcon: 'checkmark',
      },
    }).afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.changeSingleUserStatus(row.id, UserListTabs.Inactive);
      }
    });
  }

  private onRowContextDeactivateClick(row: UserModel): void {
    this.dialog.open(WarningModalComponent, {
      data: {
        title: 'MODAL.DEACTIVATE',
        message: this.translateService.instant('TEAM.DEACTIVATE_USER', { firstName: row.firstName, lastName: row.lastName }),
        confirmBtnIcon: 'checkmark',
      },
    }).afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.changeSingleUserStatus(row.id, UserListTabs.Inactive);
      }
    });
  }

  private onRowContextEditClick(row: UserModel): void {
    this.router.navigate([`/team/employee-profile/${row.id}`], { state: { setEditState: true, documentId: row.id } });
  }

  private changeSingleUserStatus(userId: number, status: UserListTabs): void {
    this.hrmService.changeUserStatus(userId, status)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getEmployeeList(true));
  }

  public createNewEmployee() {
    if (this.createNewEmployeeRequest$.getValue()) { return; }
    if (this.actionButtonsService.isActionDisabledByFreePlan(SbcPermissionEnum.TEAM_USERS, this.createNewEmployee)) { return; }

    this.createNewEmployeeRequest$.next(true);

    const dialogRef = this.dialog.open(CreateNewEmployeeModalComponent, {disableClose: true,});

    dialogRef.afterClosed()
      .pipe(finalize(() => this.createNewEmployeeRequest$.next(false)))
      .subscribe(res => {
        if (res) {
          this.getEmployeeList();
        }
      });
  }

  public displayMessage(message: string, type: string = 'success') {
    this.toasterService.notify({ type, message });
  }

  public selectedRowsReciever(rows) {
    this.selectedRows = rows;
  }

  public onActionReceived(btnAction: string): void {
    if (this[btnAction]) {
      this[btnAction]();
    }
  }

  private getBulkModalParams(): { quantity: number; docType: string } {
    const quantity = this.selectedRows.length;
    return { quantity, docType: getBulkDocTypeName(this.translateService, 'EMPLOYEE', quantity) };
  }

  public onDeletePermanentlyUserClick(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: {
        title: 'MODAL.DELETE_PERMANENTLY',
        message: this.translateService.instant('TEAM.DELETE_PERMANENTLY_USERS', this.getBulkModalParams()),
        confirmBtnText: 'BUTTON.DELETE_PERMANENTLY',
        confirmBtnIcon: 'trash-2'
      }
    });

    dialog.afterClosed()
      .subscribe(response => {
        if (response !== CommonModalsActionsEnum.CONFIRM) {
          this.btnToClearLoadingStatus$.next('onDeletePermanentlyUserClick');
          return;
        }
        const ids = this.bulkActionableSelectedRows.map((user: UserModel) => user.id);
        this.hrmService.deleteUsers(ids)
          .pipe(
            finalize(() => {
              this.selectedRows = [];
              this.btnToClearLoadingStatus$.next('onDeletePermanentlyUserClick');
            }),
            takeUntil(this.destroy$)
          )
          .subscribe(() => {
            this.getEmployeeList(true);
          });
      });
  }

  public onDeleteUserClick(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: {
        title: 'MODAL.DELETE',
        message: this.translateService.instant('TEAM.DELETE_USERS', this.getBulkModalParams()),
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2'
      }
    });

    dialog.afterClosed()
      .subscribe(response => {
        if (response === CommonModalsActionsEnum.CONFIRM) {
          this.changeUsersStatus(UserListTabs.Deleted, false, undefined, 'onDeleteUserClick');
          return;
        }
        this.btnToClearLoadingStatus$.next('onDeleteUserClick');
      });
  }

  public onRestoreUserClick(): void {
    if (this.actionButtonsService.isActionDisabledByFreePlan(SbcPermissionEnum.TEAM_USERS, this.onRestoreUserClick)) {
      this.btnToClearLoadingStatus$.next('onRestoreUserClick');
      return;
    }

    const dialog = this.dialog.open(WarningModalComponent, {
      data: {
        title: 'MODAL.RESTORE',
        message: this.translateService.instant('TEAM.RESTORE_USERS', this.getBulkModalParams()),
        confirmBtnText: 'BUTTON.RESTORE',
        confirmBtnIcon: 'checkmark',
      }
    });

    dialog.afterClosed()
      .subscribe(response => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.changeUsersStatus(UserListTabs.Inactive, false, undefined, 'onRestoreUserClick');
        return;
      }
      this.btnToClearLoadingStatus$.next('onRestoreUserClick');
    });
  }

  public onDeactivateUserClick(): void {
    const dialog = this.dialog.open(WarningModalComponent, {
      data: {
        title: 'MODAL.DEACTIVATE',
        message: this.translateService.instant('TEAM.DEACTIVATE_USERS', this.getBulkModalParams()),
        confirmBtnIcon: 'checkmark',
      }
    });

    dialog.afterClosed()
      .subscribe(response => {
        if (response === CommonModalsActionsEnum.CONFIRM) {
          this.changeUsersStatus(UserListTabs.Inactive, false, undefined, 'onDeactivateUserClick');
          return;
        }
        this.btnToClearLoadingStatus$.next('onDeactivateUserClick');
    });
  }

  public onActivateUserClick(): void {
    if (this.actionButtonsService.isActionDisabledByFreePlan(SbcPermissionEnum.TEAM_USERS, this.onActivateUserClick)) {
      this.btnToClearLoadingStatus$.next('onActivateUserClick');
      return;
    }

    const dialog = this.dialog.open(WarningModalComponent, {
      data: {
        title: 'MODAL.ACTIVATE',
        message: this.translateService.instant('TEAM.ACTIVATE_USERS', this.getBulkModalParams()),
        confirmBtnIcon: 'checkmark',
      }
    });

    dialog.afterClosed()
      .subscribe(response => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.changeUsersStatus(UserListTabs.Active, false, undefined, 'onActivateUserClick');
        return;
      }
      this.btnToClearLoadingStatus$.next('onActivateUserClick');
    });
  }

  public changeUsersStatus(status: UserListTabs, skipWarnings = false, user_ids?, btnName?: string): void {
    const ids = this.bulkActionableSelectedRows.map((user: UserModel) => user.id);
    this.hrmService.changeUsersStatus({ user_ids: ids.length ? ids : user_ids, status, skipWarnings })
      .pipe(
        finalize(() => {
          this.selectedRows = [];
          if (btnName) { this.btnToClearLoadingStatus$.next(btnName); }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.getEmployeeList(true);
      }, error => {
        this.handlePopupErrors(error, status, ids);
      });
  }

  private handlePopupErrors(error: HttpErrorResponse, status: UserListTabs, user_ids?): void {
    switch (error.error.message) {
      default:
        this.displayMessage(error.error.message, 'error');
        break;
    }
  }

  public goToPage(page: number): void {
    const url = this.getNewPaginationUrl(this.router.url);

    this.pagination = { ...this.pagination, page: page + 1 };
    this.router.navigate([url, this.pagination.page]);
  }

  public getNewPaginationUrl(oldUrl: string): string {
    return oldUrl.split('/').slice(0, -1).join('/');
  }

  public rowClickReceiver(event): void {
    if (event.type === TableActivateTypes.Link) {
      this.router.navigate([`/team/employee-profile/${event.row.id}`]);
    }
  }

  public onExportCsvClick(): void {
    if (this.exportCsvRequest$.getValue()) { return; }
    this.exportCsvRequest$.next(true);

    this.hrmService.getUsersListExport(this.activeStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((fileParams: FileUploadParams) => {
        this.downloadFile(fileParams.url);
      });
  }

  public downloadFile(url) {
    this.fileService.downloadFile(url)
      .pipe(
        finalize(() => { this.exportCsvRequest$.next(false); }),
        takeUntil(this.destroy$),
      )
      .subscribe((res: any) => {
        this.fileSaverService.save(
          res.body,
          getContentDispositionFileName(res.headers.get('Content-Disposition'))
        );
      });
  }

  public sortTable(e): void {
    let sortedProp = e.column.prop;

    if (sortedProp === 'nameData') {
      sortedProp = 'name';
    }

    this.sort = {nameColumn: sortedProp, value: e.newValue.toUpperCase()};
    this.saveUserSorting(sortedProp, e.newValue.toUpperCase());
  }

  public saveUserSorting(sortBy: string, direction: 'ASC'|'DESC'): void {
    const sorting = {
      sortBy,
      direction,
      type: DocumentTypesUppercaseEnum.USER,
      status: this.activeStatus
    }
    this.userSortingService.saveUserSorting(sorting)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getEmployeeList(true));
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  get activeStatus(): UserListTabs {
    return this.activeStatus$.getValue();
  }

  public get showSummaryBar(): boolean {
    return this.tableSummaryBarItems.length && this.activeStatus === UserListTabs.Active;
  }
  public hideSummaryBar(): boolean {
    return this.activeStatus !== UserListTabs.Active;
  }

}
